import React, {useCallback, useEffect, useRef} from "react";
import {Link as RouterLink} from "react-router-dom";
import {
    AspectRatio,
    Box,
    Flex,
    IconButton,
    Image,
    Link,
    Modal,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Text,
} from "@chakra-ui/react";
import {ArrowForwardIcon, ChevronLeftIcon, ChevronRightIcon} from "@chakra-ui/icons";

// Full screen viewer for photos and videos.
// items: [{type: "image", src, title?, subtitle?, href?} | {type: "video", src?, embed?, poster?, title?, subtitle?, href?}]
function Media({item}) {
    if (item.type === "video") {
        if (item.embed) {
            return (
                <AspectRatio ratio={16 / 9} w="min(100%, 1100px)">
                    <iframe src={item.embed} title={item.title || "Video"} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen/>
                </AspectRatio>
            );
        }
        return (
            <Box as="video" key={item.src} src={item.src} poster={item.poster} controls autoPlay playsInline
                 maxH="calc(100vh - 170px)" maxW="100%" bg="black"/>
        );
    }
    return (
        <Image key={item.src} src={item.src} alt={item.title || ""} maxH="calc(100vh - 170px)" maxW="100%"
               objectFit="contain" userSelect="none" draggable={false}/>
    );
}

function Lightbox({items, index, onClose, onChange}) {
    const isOpen = index !== null && index !== undefined && index >= 0 && index < items.length;
    const count = items.length;
    const touchX = useRef(null);

    const prev = useCallback(() => onChange((index - 1 + count) % count), [index, count, onChange]);
    const next = useCallback(() => onChange((index + 1) % count), [index, count, onChange]);

    useEffect(() => {
        if (!isOpen || count < 2) return undefined;
        const onKey = (e) => {
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isOpen, count, prev, next]);

    if (!isOpen) return null;
    const item = items[index];

    const arrowStyle = {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        size: "lg",
        variant: "ghost",
        color: "white",
        fontSize: "3xl",
        bg: {base: "blackAlpha.500", md: "transparent"},
        _hover: {bg: "whiteAlpha.200"},
    };

    return (
        <Modal isOpen onClose={onClose} size="full" motionPreset="none">
            <ModalOverlay bg="rgba(10,10,10,0.94)"/>
            <ModalContent bg="transparent" boxShadow="none" m={0} borderRadius={0}>
                <ModalCloseButton color="white" size="lg" top={4} right={4} zIndex={2}/>
                <Flex
                    minH="100vh"
                    direction="column"
                    align="center"
                    justify="center"
                    px={{base: 2, md: 20}}
                    py={{base: 16, md: 10}}
                    onClick={(e) => e.target === e.currentTarget && onClose()}
                    onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
                    onTouchEnd={(e) => {
                        if (touchX.current === null || count < 2) return;
                        const dx = e.changedTouches[0].clientX - touchX.current;
                        if (Math.abs(dx) > 50) (dx > 0 ? prev : next)();
                        touchX.current = null;
                    }}
                >
                    <Media item={item}/>
                    <Box mt={4} textAlign="center" color="whiteAlpha.800" px={4}>
                        {count > 1 && (
                            <Text fontSize="xs" letterSpacing="0.16em" color="whiteAlpha.600">{index + 1} / {count}</Text>
                        )}
                        {item.title && <Text color="white" fontWeight={600} mt={1}>{item.title}</Text>}
                        {item.subtitle && <Text fontSize="sm">{item.subtitle}</Text>}
                        {item.href && (
                            <Link as={RouterLink} to={item.href} onClick={onClose} fontSize="xs" fontWeight={700}
                                  textTransform="uppercase" letterSpacing="0.14em" color="brand.200" display="inline-flex"
                                  alignItems="center" gap={1} mt={2} _hover={{color: "white"}}>
                                View project <ArrowForwardIcon/>
                            </Link>
                        )}
                    </Box>
                </Flex>
                {count > 1 && (
                    <>
                        <IconButton aria-label="Previous" icon={<ChevronLeftIcon/>} left={{base: 1, md: 4}} onClick={prev} {...arrowStyle}/>
                        <IconButton aria-label="Next" icon={<ChevronRightIcon/>} right={{base: 1, md: 4}} onClick={next} {...arrowStyle}/>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
}

export default Lightbox;
